import { useState } from "react";
import { Check, Search } from "lucide-react";
import { useDevicesForOrganization } from "@/hooks/use-devices-for-organization";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Checkbox } from "@/components/ui/checkbox";

interface DeviceMultiSelectProps {
  organizationId: string | undefined;
  selectedDeviceIds: string[];
  onSelectionChange: (deviceIds: string[]) => void;
  disabled?: boolean;
}

export const DeviceMultiSelect = ({
  organizationId,
  selectedDeviceIds,
  onSelectionChange,
  disabled = false,
}: DeviceMultiSelectProps) => {
  const [searchQuery, setSearchQuery] = useState("");
  const { data: devices, isLoading, error } = useDevicesForOrganization(organizationId);

  const query = searchQuery.toLowerCase();
  const filteredDevices = devices?.filter(
    (device) =>
      device.device_name?.toLowerCase().includes(query) ||
      device.device_description?.toLowerCase().includes(query) ||
      device.device_serial?.toLowerCase().includes(query) ||
      device.device_id?.toLowerCase().includes(query)
  );

  const handleToggleDevice = (deviceId: string) => {
    if (selectedDeviceIds.includes(deviceId)) {
      onSelectionChange(selectedDeviceIds.filter((id) => id !== deviceId));
    } else {
      onSelectionChange([...selectedDeviceIds, deviceId]);
    }
  };

  const handleSelectAll = () => {
    if (filteredDevices) {
      onSelectionChange(filteredDevices.map((d) => d.device_id));
    }
  };

  const handleClearAll = () => {
    onSelectionChange([]);
  };

  if (!organizationId) {
    return (
      <div className="text-sm text-muted-foreground">
        Organization required for device assignment
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="space-y-2">
        <div className="h-4 w-32 bg-muted animate-pulse rounded" />
        <div className="h-20 bg-muted animate-pulse rounded" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-sm text-destructive">
        Failed to load devices. Please try again.
      </div>
    );
  }

  if (!devices || devices.length === 0) {
    return (
      <div className="text-sm text-muted-foreground">
        No devices available in this organization
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-2">
        <Label className="text-sm font-medium">
          Assign Devices (Optional)
          {selectedDeviceIds.length > 0 && (
            <Badge variant="secondary" className="ml-2">
              {selectedDeviceIds.length} selected
            </Badge>
          )}
        </Label>
        <div className="flex gap-2">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={handleSelectAll}
            disabled={disabled || !filteredDevices || filteredDevices.length === 0}
          >
            Select All
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={handleClearAll}
            disabled={disabled || selectedDeviceIds.length === 0}
          >
            Clear
          </Button>
        </div>
      </div>

      <p className="text-xs text-muted-foreground">
        Select devices to assign to this user upon invitation acceptance
      </p>

      <div className="relative">
        <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Search devices..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-8"
          disabled={disabled}
        />
      </div>

      <ScrollArea className="h-[200px] border rounded-md">
        <div className="p-2 space-y-1">
          {filteredDevices && filteredDevices.length > 0 ? (
            filteredDevices.map((device) => (
              <div
                key={device.device_id}
                className="flex items-start gap-2 p-2 hover:bg-accent rounded-md cursor-pointer"
                onClick={() => !disabled && handleToggleDevice(device.device_id)}
              >
                <Checkbox
                  checked={selectedDeviceIds.includes(device.device_id)}
                  disabled={disabled}
                  className="mt-0.5"
                />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-medium truncate">
                      {device.device_name || device.device_id}
                    </p>
                    {selectedDeviceIds.includes(device.device_id) && (
                      <Check className="h-4 w-4 text-primary flex-shrink-0" />
                    )}
                  </div>
                  {(device.device_serial || device.device_description) && (
                    <p className="text-xs text-muted-foreground truncate">
                      {[device.device_serial, device.device_description]
                        .filter(Boolean)
                        .join(" · ")}
                    </p>
                  )}
                </div>
              </div>
            ))
          ) : (
            <div className="text-sm text-muted-foreground text-center py-8">
              No devices found
            </div>
          )}
        </div>
      </ScrollArea>
    </div>
  );
};
